import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Auth from '../../../api/auth';
import { useUserData } from '../../../redux/selectors';
import { fetchUserDataFulfilled } from '../../../redux/user/actions';

import type { User } from '@/types/user';

// Reloads the signed-in user. Never throws: callers proceed with the current state on failure.
const useRefreshUser = (): (() => Promise<void>) => {
  const dispatch = useDispatch();
  const { email } = useUserData();

  return useCallback(async (): Promise<void> => {
    if (!email) {
      return;
    }

    try {
      const { data } = (await Auth.fetchV2Users({ email })) as {
        data: Record<string, unknown>[] | Record<string, unknown>;
      };
      const freshUser = Array.isArray(data) ? data[0] : data;

      if (freshUser) {
        // /users omits onboardingStatus for non-privileged, onboarded users, and the user
        // reducer merges, so the old status would survive. Absent means onboarded.
        dispatch(
          fetchUserDataFulfilled({
            ...freshUser,
            onboardingStatus: freshUser.onboardingStatus ?? 'onboarded',
          } as unknown as User)
        );
      }
    } catch {
      // Keep the current user; the next login refreshes it anyway.
    }
  }, [dispatch, email]);
};

export default useRefreshUser;
